import { authFetch } from '../../../api/csrf';
import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import customerActivityService from "../../../api/crm/customers/customerActivityService";
import configService from "../../../api/crm/config/configService";
import { useQuery } from "@tanstack/react-query";
import { toast } from 'react-hot-toast';
import {
  Plus,
  Phone,
  Mail,
  MessageSquare,
  Calendar,
  FileText,
  Clock,
  User,
  Filter,
  Search,
  Download,
  Eye,
  Edit,
  Trash2,
  X,
  XCircle,
  CheckCircle,
  AlertCircle,
  Building,
  ChevronRight,
  ChevronDown,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
} from "lucide-react";
import Button from "../../../components/common/Button/Button";
import CustomerSelectionModal from "../../../components/crm/CustomerSelectionModal";
import {
  formatDate,
} from "../../../utils/stringUtils";
import {
  sanitizeSearchInput,
  sanitizeDescriptionInput,
  handleSearchInputChange,
  getCharacterCount,
  FIELD_LIMITS,
} from "../../../utils/inputValidation";
import PermissionGate from "../../../components/common/PermissionGate";
import ExportModal from "../../../components/common/ExportModal";
import SelectedExportFormatBar from "../../../components/common/SelectedExportFormatBar";
import { API_ENDPOINTS } from "../../../api/config";
import PaginationControls from "../../../components/common/Pagination/PaginationControls";
import DateRangeFilter from "../../../components/common/DateRangeFilter";

const CustomerActivities = ({ onViewCustomer }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const userRoleNormalized = (user?.role || '').toLowerCase().replace(/[\s-]/g, '_');
  const isSalesExecutive = userRoleNormalized === 'sales_executive' || userRoleNormalized === 'export_executive' || userRoleNormalized === 'business_relationship_manager' || userRoleNormalized === 'business_development_executive';
  const isManager = userRoleNormalized === 'sales_manager' || userRoleNormalized === 'export_manager';
  const currentUserId = user?.id || user?.userId || user?._id;
  const defaultAssignee = isManager && currentUserId ? currentUserId : 'all';

  // Initial filter values from URL (for persistence across refresh)
  const initialTab = searchParams.get("tab") || "all";
  const initialSearch = searchParams.get("search") || "";
  const initialOutcome = searchParams.get("outcome") || "all";
  const initialNature = searchParams.get("nature") || "all";
  const initialAssignee = searchParams.get("assignee") || defaultAssignee;
  const initialStartDate = searchParams.get("startDate") || "";
  const initialEndDate = searchParams.get("endDate") || "";
  const initialPage = Number(searchParams.get("page")) || 1;
  const initialLimit = Number(searchParams.get("limit")) || 10;
  const initialSortBy = searchParams.get("sortBy") || "performedAt";
  const initialSortOrder = searchParams.get("sortOrder") || "desc";
  const isInitialFilterMount = useRef(true);

  const [activeTab, setActiveTab] = useState(initialTab);
  const [searchTerm, setSearchTerm] = useState(initialSearch);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(initialSearch);
  const [dateRange, setDateRange] = useState({ startDate: initialStartDate, endDate: initialEndDate });
  const [assigneeFilter, setAssigneeFilter] = useState(initialAssignee);
  const [assigneeOptions, setAssigneeOptions] = useState([]);
  const [filteredAssigneeOptions, setFilteredAssigneeOptions] = useState([]);
  const [loadingAssignees, setLoadingAssignees] = useState(false);
  const [assigneeSearchTerm, setAssigneeSearchTerm] = useState("");
  const [openAssigneeDropdown, setOpenAssigneeDropdown] = useState(false);
  const [assigneeDropdownPosition, setAssigneeDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const assigneeDropdownRef = useRef(null);
  const [showCustomerSelectionModal, setShowCustomerSelectionModal] = useState(false);

  // API state
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalActivities: 0,
    hasNextPage: false,
    hasPrevPage: false,
    limit: 10,
  });
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [itemsPerPage, setItemsPerPage] = useState(initialLimit);
  const [sortBy, setSortBy] = useState(initialSortBy);
  const [sortOrder, setSortOrder] = useState(initialSortOrder);
  const [outcomeFilter, setOutcomeFilter] = useState(initialOutcome);
  const [natureFilter, setNatureFilter] = useState(initialNature);
  const [deletingActivityId, setDeletingActivityId] = useState(null);

  // New states for export and selection
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [showInlineSelectedFormats, setShowInlineSelectedFormats] = useState(false);
  const [inlineFormatPanelEntered, setInlineFormatPanelEntered] = useState(false);
  const selectedExportWrapRef = useRef(null);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);


  const { data: activityStatuses = [] } = useQuery({
    queryKey: ['activityStatuses'],
    queryFn: async () => {
      const response = await configService.activityStatusService.getDropdown();
      if (response.success) {
        return response.data || [];
      }
      return [];
    }
  });

  const getUserDisplayName = (person) => {
    if (!person) return "Unknown User";
    if (typeof person === "string") return person;
    if (person.name) return person.name;
    if (person.firstName && person.lastName) {
      return `${person.firstName} ${person.lastName}`;
    }
    if (person.firstName) return person.firstName;
    if (person.lastName) return person.lastName;
    if (person.email) return person.email;
    return "Unknown User";
  };

  const getAssigneeName = (performedBy) => {
    const name = getUserDisplayName(performedBy);
    return name === "Unknown User" ? "N/A" : name;
  };

  const loadAssignees = useCallback(async () => {
    if (isSalesExecutive) return;
    setLoadingAssignees(true);
    try {
      const params = new URLSearchParams();
      params.append("limit", "100");
      params.append("roleFilter", "sales");

      if (isManager) {
        params.append("managerId", currentUserId);
      }

      const url = `${API_ENDPOINTS.CRM.TEAM_MANAGEMENT.USERS}?${params.toString()}`;
      const response = await authFetch(url, {
        credentials: 'include',
        method: "GET",
        headers: {
          "Content-Type": "application/json"
        }
      });

      const result = await response.json();
      if (result.responseCode === 200) {
        const crmUsers = result.data?.users || [];

        if (isManager) {
          // Map subordinate executives
          const executives = crmUsers
            .filter((u) => ["sales_executive", "export_executive", "business_relationship_manager", "business_development_executive"].includes((u.role || "").toLowerCase()))
            .map((u) => ({
              id: u.user || u._id || u.id,
              name: getUserDisplayName(u),
              email: u.email || "",
              role: u.role || ""
            }))
            .sort((a, b) => a.name.localeCompare(b.name));

          const selfOption = {
            id: currentUserId,
            name: `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || user?.name || 'Me',
            email: user?.email || '',
            role: user?.role || ''
          };

          const myTeamOption = {
            id: 'my_team',
            name: 'My Team',
            email: '',
            role: 'system'
          };

          const allOptions = [
            myTeamOption,
            { id: 'unassigned', name: 'Unassigned', role: 'system' },
            selfOption,
            ...executives
          ];
          setAssigneeOptions(allOptions);
          setFilteredAssigneeOptions(allOptions);
        } else {
          // SUPER ADMIN / others
          const mappedUsers = crmUsers.map((u) => ({
            id: u.user || u._id || u.id,
            name: getUserDisplayName(u),
            email: u.email || "",
            role: u.role || ""
          })).sort((a, b) => a.name.localeCompare(b.name));

          const allOptions = [
            { id: 'unassigned', name: 'Unassigned', role: 'system' },
            ...mappedUsers
          ];
          setAssigneeOptions(allOptions);
          setFilteredAssigneeOptions(allOptions);
        }
      } else {
        setAssigneeOptions([]);
        setFilteredAssigneeOptions([]);
      }
    } catch (error) {
      setAssigneeOptions([]);
      setFilteredAssigneeOptions([]);
    } finally {
      setLoadingAssignees(false);
    }
  }, [user, isSalesExecutive, isManager, currentUserId]);

  // Debounced backend search for assignee dropdown
  useEffect(() => {
    if (!openAssigneeDropdown) return;

    // Always show full list when search is empty
    if (assigneeSearchTerm.trim() === '') {
      setFilteredAssigneeOptions(assigneeOptions);
      return;
    }

    const handler = setTimeout(async () => {
      setLoadingAssignees(true);
      try {
        const params = new URLSearchParams();
        params.append('search', assigneeSearchTerm.trim());
        params.append('limit', '50');
        params.append('roleFilter', 'sales');
        if (isManager) {
          params.append('managerId', currentUserId);
        }

        const url = `${API_ENDPOINTS.CRM.TEAM_MANAGEMENT.USERS}?${params.toString()}`;
        const response = await authFetch(url, {
          credentials: 'include',
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        });
        const result = await response.json();

        if (result.responseCode === 200) {
          const crmUsers = result.data?.users || [];
          const searchLower = assigneeSearchTerm.trim().toLowerCase();

          // Always keep system options that match the search term at the top
          const systemOptions = assigneeOptions.filter(o =>
            o.role === 'system' &&
            (o.name || '').toLowerCase().includes(searchLower)
          );

          const mappedUsers = crmUsers
            .filter((u) => isManager
              ? ['sales_executive', 'export_executive', 'business_relationship_manager', 'business_development_executive'].includes((u.role || '').toLowerCase())
              : true
            )
            .map((u) => ({
              id: u.user || u._id || u.id,
              name: getUserDisplayName(u),
              email: u.email || '',
              role: u.role || ''
            }));

          setFilteredAssigneeOptions([...systemOptions, ...mappedUsers]);
        }
      } catch {
        // On error fall back to local filter so the dropdown stays usable
        const searchLower = assigneeSearchTerm.trim().toLowerCase();
        setFilteredAssigneeOptions(
          assigneeOptions.filter(o =>
            (o.name || '').toLowerCase().includes(searchLower) ||
            (o.email || '').toLowerCase().includes(searchLower)
          )
        );
      } finally {
        setLoadingAssignees(false);
      }
    }, 350);

    return () => clearTimeout(handler);
  }, [assigneeSearchTerm, openAssigneeDropdown, assigneeOptions, isManager, currentUserId]);

  useEffect(() => {
    if (openAssigneeDropdown && assigneeDropdownRef.current) {
      const rect = assigneeDropdownRef.current.getBoundingClientRect();
      setAssigneeDropdownPosition({
        top: rect.bottom + window.scrollY + 8,
        left: rect.left + window.scrollX,
        width: rect.width
      });
    }
  }, [openAssigneeDropdown]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (assigneeDropdownRef.current && !assigneeDropdownRef.current.contains(event.target)) {
        const dropdownElement = document.querySelector('[data-dropdown-assignee-filter]');
        if ((dropdownElement && !dropdownElement.contains(event.target)) || !dropdownElement) {
          setOpenAssigneeDropdown(false);
          setAssigneeSearchTerm("");
        }
      }
    };

    if (openAssigneeDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [openAssigneeDropdown]);


  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return (
      debouncedSearchTerm.trim() !== "" ||
      activeTab !== "all" ||
      outcomeFilter !== "all" ||
      natureFilter !== "all" ||
      assigneeFilter !== defaultAssignee ||
      dateRange.startDate !== "" ||
      dateRange.endDate !== ""
    );
  }, [debouncedSearchTerm, activeTab, outcomeFilter, natureFilter, assigneeFilter, defaultAssignee, dateRange]);

  const baseExportFilters = useMemo(() => {
    const f = {};
    if (activeTab !== "all") f.type = activeTab;
    if (outcomeFilter !== "all") f.outcome = outcomeFilter;
    if (natureFilter !== "all") f.activityNature = natureFilter;
    if (debouncedSearchTerm.trim()) f.search = debouncedSearchTerm.trim();
    if (assigneeFilter !== "all") f.performedBy = assigneeFilter;
    if (dateRange.startDate) f.startDate = dateRange.startDate;
    if (dateRange.endDate) f.endDate = dateRange.endDate;
    f.dateField = natureFilter === 'followup' ? 'performedAt' : 'createdAt';
    return f;
  }, [
    activeTab,
    outcomeFilter,
    natureFilter,
    debouncedSearchTerm,
    assigneeFilter,
    dateRange.startDate,
    dateRange.endDate,
  ]);

  // Helper function to get the correct activity time
  // Priority: performedAt (when call was initiated) > callMetadata times > createdAt
  // This ensures consistent time display before and after webhook updates
  const getActivityTime = (activity) => {
    if (!activity) return new Date().toISOString();

    if (activity.activityNature === 'followup' && activity.scheduledFor) {
      return new Date(activity.scheduledFor).toISOString();
    }

    // Always prioritize performedAt first - this is when the call was actually initiated
    if (activity.performedAt) {
      return new Date(activity.performedAt).toISOString();
    }

    if (activity.scheduledFor) {
      return new Date(activity.scheduledFor).toISOString();
    }

    // For automated calls, fallback to callMetadata times if performedAt is not available
    if (activity.isAutomated && activity.callMetadata) {
      // Priority order: startTime > legBStartTime > legAPickedTime > legBPickedTime
      const callTime =
        activity.callMetadata.startTime ||
        activity.callMetadata.legBStartTime ||
        activity.callMetadata.legAPickedTime ||
        activity.callMetadata.legBPickedTime;

      if (callTime) {
        try {
          const date = new Date(callTime);
          if (!isNaN(date.getTime())) {
            return date.toISOString();
          }
        } catch (error) {
        }
      }
    }

    // Final fallback to createdAt
    return activity.createdAt || new Date().toISOString();
  };

  // Sorting handler
  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortOrder("asc");
    }
  };

  const getSortedActivities = () => {
    if (!sortBy || !activities || activities.length === 0) return activities;

    return [...activities].sort((a, b) => {
      let aValue, bValue;

      if (sortBy === "activityId") {
        aValue = (a.activityId || "").toLowerCase();
        bValue = (b.activityId || "").toLowerCase();
      } else if (sortBy === "customerId") {
        aValue = a.customer?.customerId || a.customer?.id || "";
        bValue = b.customer?.customerId || b.customer?.id || "";
      } else if (sortBy === "customerName") {
        aValue = getCustomerName(a.customer).toLowerCase();
        bValue = getCustomerName(b.customer).toLowerCase();
      } else if (sortBy === "company") {
        aValue = (a.customer?.companyName || "").toLowerCase();
        bValue = (b.customer?.companyName || "").toLowerCase();
      } else if (sortBy === "email") {
        aValue = (a.customer?.email || "").toLowerCase();
        bValue = (b.customer?.email || "").toLowerCase();
      } else if (sortBy === "title") {
        aValue = (a.title || "").toLowerCase();
        bValue = (b.title || "").toLowerCase();
      } else if (sortBy === "type") {
        aValue = (a.type || "").toLowerCase();
        bValue = (b.type || "").toLowerCase();
      } else if (sortBy === "assignee") {
        aValue = getAssigneeName(a.performedBy).toLowerCase();
        bValue = getAssigneeName(b.performedBy).toLowerCase();
      } else if (sortBy === "performedAt") {
        aValue = new Date(getActivityTime(a)).getTime();
        bValue = new Date(getActivityTime(b)).getTime();
      } else if (sortBy === "createdAt") {
        aValue = new Date(a.createdAt || 0).getTime();
        bValue = new Date(b.createdAt || 0).getTime();
      } else if (sortBy === "status") {
        aValue = (a.status || a.outcome || "completed").toLowerCase();
        bValue = (b.status || b.outcome || "completed").toLowerCase();
      } else {
        aValue = a[sortBy] || "";
        bValue = b[sortBy] || "";
      }

      if (typeof aValue === "string") aValue = aValue.toLowerCase();
      if (typeof bValue === "string") bValue = bValue.toLowerCase();

      if (aValue < bValue) return sortOrder === "asc" ? -1 : 1;
      if (aValue > bValue) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });
  };

  const sortedActivitiesForExport = useMemo(
    () => getSortedActivities(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activities, sortBy, sortOrder],
  );

  const currentPageActivityIds = useMemo(
    () =>
      sortedActivitiesForExport
        .map((a) => String(a._id || a.id || a.activityId || ""))
        .filter(Boolean),
    [sortedActivitiesForExport],
  );

  // Clear all filters and search
  const handleClearFilters = () => {
    setSearchTerm("");
    setDebouncedSearchTerm("");
    setActiveTab("all");
    setOutcomeFilter("all");
    setNatureFilter("all");
    setAssigneeFilter(defaultAssignee);
    setDateRange({ startDate: "", endDate: "" });
    setCurrentPage(1);
    setSortBy("performedAt");
    setSortOrder("desc");
  };

  // Fetch activities from API
  const fetchActivities = useCallback(async () => {
    setLoading(true);
    try {
      const filters = {
        type: activeTab !== "all" ? activeTab : undefined,
        outcome: outcomeFilter !== "all" ? outcomeFilter : undefined,
        activityNature: natureFilter !== "all" ? natureFilter : undefined,
        search: debouncedSearchTerm.trim() || undefined,
        performedBy: assigneeFilter !== "all" ? assigneeFilter : undefined,
        page: currentPage,
        limit: itemsPerPage,
        sortBy: sortBy || "performedAt",
        sortOrder: sortOrder || "desc",
      };

      if (dateRange.startDate) filters.startDate = dateRange.startDate;
      if (dateRange.endDate) filters.endDate = dateRange.endDate;
      filters.dateField = natureFilter === 'followup' ? 'performedAt' : 'createdAt';

      // Remove undefined values
      Object.keys(filters).forEach(
        (key) => filters[key] === undefined && delete filters[key],
      );

      const result = await customerActivityService.getAllActivities(filters);

      if (result.success) {
        setActivities(result.activities || []);
        setPagination(
          result.pagination || {
            currentPage: 1,
            totalPages: 1,
            totalActivities: 0,
            hasNextPage: false,
            hasPrevPage: false,
            limit: itemsPerPage,
          },
        );
      } else {
        toast.error(result.error || "Failed to load activities");
        setActivities([]);
      }
    } catch (error) {
      toast.error("An error occurred while loading activities");
      setActivities([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, outcomeFilter, natureFilter, assigneeFilter, debouncedSearchTerm, dateRange, currentPage, itemsPerPage, sortBy, sortOrder]);

  useEffect(() => {
    // Skip on initial mount so the URL-restored page is preserved on refresh
    if (isInitialFilterMount.current) {
      isInitialFilterMount.current = false;
      return;
    }
    const timeoutId = setTimeout(() => {
      setCurrentPage(1);
      setSelectedRows(new Set()); // Reset selection on search/tab change
    }, 500); // 500ms debounce

    return () => clearTimeout(timeoutId);
  }, [debouncedSearchTerm, activeTab, outcomeFilter, natureFilter, assigneeFilter, dateRange]);

  // Sync filter/pagination state to URL for persistence across refresh
  useEffect(() => {
    setSearchParams(prev => {
      const params = new URLSearchParams(prev);
      const managedKeys = ['tab', 'search', 'outcome', 'nature', 'assignee', 'startDate', 'endDate', 'page', 'limit', 'sortBy', 'sortOrder'];
      managedKeys.forEach(key => params.delete(key));

      if (activeTab && activeTab !== 'all') params.set('tab', activeTab);
      if (debouncedSearchTerm.trim()) params.set('search', debouncedSearchTerm.trim());
      if (outcomeFilter && outcomeFilter !== 'all') params.set('outcome', outcomeFilter);
      if (natureFilter && natureFilter !== 'all') params.set('nature', natureFilter);
      if (assigneeFilter && assigneeFilter !== defaultAssignee) params.set('assignee', assigneeFilter);
      if (dateRange.startDate) params.set('startDate', dateRange.startDate);
      if (dateRange.endDate) params.set('endDate', dateRange.endDate);
      if (currentPage && currentPage !== 1) params.set('page', String(currentPage));
      if (itemsPerPage && itemsPerPage !== 10) params.set('limit', String(itemsPerPage));
      if (sortBy && sortBy !== 'performedAt') params.set('sortBy', sortBy);
      if (sortOrder && sortOrder !== 'desc') params.set('sortOrder', sortOrder);

      return params;
    }, { replace: true });
  }, [
    activeTab,
    debouncedSearchTerm,
    outcomeFilter,
    natureFilter,
    assigneeFilter,
    dateRange,
    currentPage,
    itemsPerPage,
    sortBy,
    sortOrder,
    defaultAssignee,
    setSearchParams,
  ]);

  // Load activities when filters, page, or items per page changes
  useEffect(() => {
    fetchActivities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, outcomeFilter, natureFilter, assigneeFilter, debouncedSearchTerm, dateRange, currentPage, itemsPerPage, sortBy, sortOrder]);

  // Reset selection when page changes
  useEffect(() => {
    setSelectedRows(new Set());
  }, [currentPage]);

  // Reset to page 1 when items per page changes
  useEffect(() => {
    setCurrentPage(1);
    setSelectedRows(new Set());
  }, [itemsPerPage]);

  useEffect(() => {
    if (selectedRows.size === 0) {
      setShowInlineSelectedFormats(false);
    }
  }, [selectedRows.size]);

  useEffect(() => {
    if (!showInlineSelectedFormats) {
      setInlineFormatPanelEntered(false);
      return;
    }
    const id = requestAnimationFrame(() =>
      requestAnimationFrame(() => setInlineFormatPanelEntered(true)),
    );
    return () => cancelAnimationFrame(id);
  }, [showInlineSelectedFormats]);

  useEffect(() => {
    if (!showInlineSelectedFormats) return;
    const onMouseDown = (e) => {
      if (
        selectedExportWrapRef.current &&
        !selectedExportWrapRef.current.contains(e.target)
      ) {
        setShowInlineSelectedFormats(false);
      }
    };
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [showInlineSelectedFormats]);

  // Debounce activity search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 400); // 400ms debounce

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    loadAssignees();
  }, [loadAssignees]);
  // Helper functions
  const displayValue = (value) => {
    if (value === null || value === undefined || value === "") return "N/A";
    return value;
  };

  const getActivityRowId = (activity) => {
    const id = activity?._id || activity?.id || activity?.activityId;
    return id == null ? "" : String(id);
  };

  const getCustomerName = (customer) => {
    if (!customer) return "Unknown Customer";
    return customer.companyName || customer.contactPerson || customer.customerId || "Unknown Customer";
  };

  const getPerformerName = (performedBy) => {
    if (!performedBy) return "Unknown User";
    if (typeof performedBy === "string") return performedBy;
    if (performedBy.firstName && performedBy.lastName) {
      return `${performedBy.firstName} ${performedBy.lastName}`;
    } else if (performedBy.firstName) {
      return performedBy.firstName;
    } else if (performedBy.email) {
      return performedBy.email;
    }
    return "Unknown User";
  };

  const getAssigneeRole = (performedBy) => {
    if (!performedBy || typeof performedBy === 'string') return '';
    return performedBy.role || '';
  };

  // Date formatting helpers using the imported formatDate
  const _formatDateOnly = (dateString) => {
    return formatDate(dateString, { includeTime: false });
  };

  const _formatTimeOnly = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleTimeString("en-IN", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    } catch (error) {
      return "N/A";
    }
  };

  const formatDuration = (minutes, callDurationSeconds = null) => {
    let totalSeconds = 0;
    if (callDurationSeconds !== null && callDurationSeconds !== undefined) {
      totalSeconds = Math.floor(callDurationSeconds);
    } else if (minutes) {
      totalSeconds = Math.floor(minutes * 60);
    } else {
      return "";
    }

    const hours = Math.floor(totalSeconds / 3600);
    const mins = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;

    const parts = [];
    if (hours > 0) parts.push(`${hours}h`);
    if (mins > 0) parts.push(`${mins}m`);
    if (secs > 0 || (hours === 0 && mins === 0)) parts.push(`${secs}s`);

    return parts.join(' ');
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case "call":
        return Phone;
      case "email":
        return Mail;
      case "whatsapp":
        return MessageSquare;
      case "meeting":
        return Calendar;
      case "note":
        return FileText;
      default:
        return Clock;
    }
  };

  const getActivityColor = (type) => {
    switch (type) {
      case "call":
        return "text-blue-600";
      case "email":
        return "text-green-600";
      case "whatsapp":
        return "text-green-500";
      case "meeting":
        return "text-purple-600";
      case "note":
        return "text-gray-600";
      default:
        return "text-gray-600";
    }
  };

  // Helper function to map RP Digital call status to UI display text
  const getCallStatusDisplay = (status) => {
    if (!status) return null;
    const statusUpper = status.toUpperCase();
    const statusMap = {
      ANSWER: "Answered",
      PICKED: "Answered", // Picked means call was answered
      NOANSWER: "Missed / No Answer",
      BUSY: "Busy",
      "CANCEL-AGENT": "Cancelled by Agent",
      "CANCEL-CUSTOMER": "Cancelled by Customer",
      FAILED: "Failed",
      PENDING: "Pending",
    };
    return statusMap[statusUpper] || status;
  };

  // Helper function to get call status badge color
  const getCallStatusColor = (status) => {
    if (!status) return "bg-gray-100 text-gray-800";
    const statusUpper = status.toUpperCase();
    switch (statusUpper) {
      case "ANSWER":
        return "bg-green-100 text-green-800";
      case "PICKED":
        return "bg-green-100 text-green-800"; // Picked means answered
      case "NOANSWER":
        return "bg-yellow-100 text-yellow-800";
      case "BUSY":
        return "bg-orange-100 text-orange-800";
      case "CANCEL-AGENT":
        return "bg-red-100 text-red-800";
      case "CANCEL-CUSTOMER":
        return "bg-red-100 text-red-800";
      case "FAILED":
        return "bg-red-100 text-red-800";
      case "PENDING":
        return "bg-blue-100 text-blue-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const handleLogActivity = () => {
    // Show customer selection modal
    setShowCustomerSelectionModal(true);
  };

  // Handle customer selection and navigate to log activity form
  const handleConfirmCustomerSelection = (customerId) => {
    setShowCustomerSelectionModal(false);
    navigate(`/log_customer_activity/${customerId}`);
  };

  // View Activity functionality - navigate to detail page
  const handleViewActivity = (activityId) => {
    navigate(`/customer_activity_detail/${activityId}`);
  };

  // Edit Activity functionality - navigate to LogActivity in edit mode
  const handleEditActivity = (activity) => {
    const activityId = getActivityRowId(activity);
    if (activity && activity.customer) {
      const customerId = activity.customer._id || activity.customer.id;
      if (customerId && activityId) {
        navigate(`/log_customer_activity/${customerId}?edit=true&activityId=${activityId}`);
      } else {
        toast.error("Cannot edit: Activity information not available");
      }
    } else {
      toast.error("Cannot edit: Customer information not available");
    }
  };

  const requestBackendExport = useCallback(
    async ({
      scope,
      format,
      selectedIds = [],
      overrideFilters = null,
      overridePage = null,
      overrideLimit = null,
      overrideSortBy = null,
      overrideSortOrder = null,
      currentPageIds: overrideCurrentPageIds = null,
    }) => {
      const pageIds =
        scope === "currentPage"
          ? (overrideCurrentPageIds ?? currentPageActivityIds)
          : [];
      const response = await authFetch(
        `${process.env.REACT_APP_API_BASE_URL}/exports/generate`, {
        credentials: 'include',
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          module: "crm-customer-activities",
          format,
          scope,
          filters: overrideFilters ?? baseExportFilters,
          page: overridePage ?? currentPage,
          limit: overrideLimit ?? itemsPerPage,
          sortBy: overrideSortBy ?? sortBy,
          sortOrder: overrideSortOrder ?? sortOrder,
          selectedIds,
          currentPageIds: Array.isArray(pageIds) ? pageIds : [],
        }),
      },
      );

      if (!response.ok) {
        const text = await response.text();
        let msg = text || "Export request failed";
        try {
          const j = JSON.parse(text);
          if (j?.responseMessage || j?.message) {
            msg = j.responseMessage || j.message;
          }
        } catch {
          /* plain text */
        }
        throw new Error(msg);
      }

      const blob = await response.blob();
      const disposition = response.headers.get("content-disposition") || "";
      const filenameMatch =
        disposition.match(/filename\*=UTF-8''([^;]+)/i) ||
        disposition.match(/filename="?([^"]+)"?/i);
      const fallbackName = `crm-customer-activities-export.${format === "xlsx" ? "xlsx" : format}`;
      const filename = filenameMatch?.[1]
        ? decodeURIComponent(filenameMatch[1]).replace(/(^"|"$)/g, "")
        : fallbackName;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    [
      baseExportFilters,
      currentPage,
      currentPageActivityIds,
      itemsPerPage,
      sortBy,
      sortOrder,
    ],
  );

  const handleCustomerActivitiesSelectedFormatExport = useCallback(
    async (format) => {
      if (!["csv", "xlsx", "pdf"].includes(format)) return;
      const ids = Array.from(selectedRows).map(String).filter(Boolean);
      if (!ids.length) {
        toast.error("Nothing selected to export.");
        return;
      }
      setExportLoading(true);
      try {
        await requestBackendExport({
          scope: "selected",
          format,
          selectedIds: ids,
        });
        toast.success(`${ids.length} row(s) exported.`);
        setShowInlineSelectedFormats(false);
      } catch (error) {
        toast.error(error?.message || "Export failed.");
      } finally {
        setExportLoading(false);
      }
    },
    [selectedRows, requestBackendExport],
  );

  const handleCustomerActivitiesScopedExport = useCallback(
    async (scope, format, meta = {}) => {
      if (!["csv", "xlsx", "pdf"].includes(format)) {
        return;
      }

      setExportLoading(true);
      try {
        const totalFiltered = Number(pagination?.totalActivities ?? 0);

        if (scope === "currentPage" && !currentPageActivityIds.length) {
          toast.error("No data on current page to export.");
          return;
        }

        if (scope === "filtered" && totalFiltered === 0) {
          toast.error("No activities match the current filters.");
          return;
        }

        if (scope === "dateRange") {
          const startDate = meta?.startDate;
          const endDate = meta?.endDate;
          if (!startDate || !endDate) {
            toast.error("Please select both start and end dates.");
            return;
          }
          await requestBackendExport({
            scope: "dateRange",
            format,
            overrideFilters: {
              ...baseExportFilters,
              startDate,
              endDate,
            },
          });
        } else if (scope === "all") {
          await requestBackendExport({
            scope: "all",
            format,
            overrideFilters: {},
            overridePage: 1,
            overrideLimit: itemsPerPage,
          });
        } else if (scope === "filtered") {
          await requestBackendExport({
            scope: "filtered",
            format,
          });
        } else if (scope === "currentPage") {
          await requestBackendExport({
            scope: "currentPage",
            format,
            currentPageIds: currentPageActivityIds,
          });
        } else {
          return;
        }

        toast.success("Export started.");
        setShowExportModal(false);
      } catch (error) {
        toast.error(error?.message || "Export failed.");
      } finally {
        setExportLoading(false);
      }
    },
    [
      baseExportFilters,
      currentPageActivityIds,
      itemsPerPage,
      pagination?.totalActivities,
      requestBackendExport,
    ],
  );

  const handleExportClick = () => {
    if (selectedRows.size > 0) {
      setShowInlineSelectedFormats((open) => !open);
      return;
    }
    setShowInlineSelectedFormats(false);
    setShowExportModal(true);
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allIds = activities.map(getActivityRowId).filter(Boolean);
      setSelectedRows(new Set(allIds));
    } else {
      setSelectedRows(new Set());
    }
  };

  const handleSelectRow = (id) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedRows(newSelected);
  };

  // Handle delete activity - EXACTLY like detail view
  const handleDeleteActivity = async (activityId) => {
    // Validate activityId first
    if (!activityId) {
      toast.error("Activity ID is missing");
      return;
    }

    // Find the activity to show details in confirmation (same as detail view)
    const activity = activities.find((a) => getActivityRowId(a) === activityId);

    if (!activity) {
      toast.error("Activity not found");
      return;
    }

    // Use same confirmation message as detail view
    const confirmMessage = `Are you sure you want to delete this activity? This action cannot be undone.`;

    // CRITICAL: Show confirmation dialog FIRST - handle both sync and async cases
    let confirmed;
    const confirmResult = window.confirm(confirmMessage);

    // Check if it's a Promise (async) or boolean (sync)
    if (confirmResult instanceof Promise) {
      confirmed = await confirmResult;
    } else {
      confirmed = confirmResult;
    }

    // If user clicked Cancel, stop here - do NOT proceed
    if (confirmed !== true && confirmed !== "true") {
      return; // User cancelled, exit immediately - NO API CALL
    }

    // ONLY if user clicked OK (confirmed === true), proceed with delete
    // Set deleting state to prevent multiple calls
    setDeletingActivityId(activityId);

    try {
      const result = await customerActivityService.deleteActivity(activityId);

      if (result.success) {
        toast.success("Activity deleted successfully");
        // Refresh the list
        fetchActivities();
      } else {
        toast.error(result.error || "Failed to delete activity");
      }
    } catch (error) {
      toast.error("An error occurred while deleting the activity");
    } finally {
      // Clear deleting state
      setDeletingActivityId(null);
    }
  };

  const handleBulkDelete = () => {
    if (selectedRows.size === 0) return;
    setShowBulkDeleteModal(true);
  };

  const confirmBulkDelete = async () => {
    setIsBulkDeleting(true);
    try {
      const selectedIds = Array.from(selectedRows);
      let successCount = 0;
      let lastError = null;

      // Activities are deleted one by one as there's no bulk delete endpoint in customerActivityService
      for (const activityId of selectedIds) {
        const result = await customerActivityService.deleteActivity(activityId);
        if (result.success) {
          successCount++;
        } else {
          lastError = result.error;
        }
      }

      if (successCount > 0) {
        toast.success(`Successfully deleted ${successCount} activities`);
        setSelectedRows(new Set());
        fetchActivities();
      }

      if (successCount < selectedIds.length) {
        toast.error(lastError || "Some activities could not be deleted");
      }
      setShowBulkDeleteModal(false);
    } catch (error) {
      toast.error("An error occurred during bulk deletion");
    } finally {
      setIsBulkDeleting(false);
    }
  };


  // Handle items per page change
  const handleItemsPerPageChange = (limit) => {
    setItemsPerPage(limit);
    setCurrentPage(1);
  };

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleViewCustomer = (customerId) => {
    if (onViewCustomer) {
      onViewCustomer(customerId);
    } else {
      navigate(`/customer_360/${customerId}`);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Customer Activities</h1>
          <p className="text-gray-600">
            Track and manage all customer interactions and activities
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <PermissionGate permission="customer_management" action="read">
            <div className="relative shrink-0" ref={selectedExportWrapRef}>
              <Button
                variant="outline"
                icon={
                  exportLoading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent" />
                  ) : (
                    <Download className="h-4 w-4" />
                  )
                }
                onClick={handleExportClick}
                disabled={exportLoading}
                className={`flex-shrink-0 ${selectedRows.size > 0 ? "bg-blue-50 border-blue-200 text-blue-600" : ""}`}
                aria-label={
                  selectedRows.size > 0
                    ? `Export ${selectedRows.size} selected activities`
                    : "Export activities"
                }
              >
                {selectedRows.size > 0 ? `Export Selected (${selectedRows.size})` : "Export"}
              </Button>
              <SelectedExportFormatBar
                show={selectedRows.size > 0 && showInlineSelectedFormats}
                panelEntered={inlineFormatPanelEntered}
                disabled={exportLoading}
                onSelect={handleCustomerActivitiesSelectedFormatExport}
              />
            </div>
          </PermissionGate>
          {selectedRows.size > 0 && (
            <PermissionGate permission="customer_management" action="delete">
              <Button
                variant="outline"
                icon={isBulkDeleting ? <Clock className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                onClick={handleBulkDelete}
                disabled={isBulkDeleting}
                className="text-red-600 border-red-200 hover:bg-red-50"
              >
                {isBulkDeleting ? "Deleting..." : `Delete (${selectedRows.size})`}
              </Button>
            </PermissionGate>
          )}
          <PermissionGate permission="customer_management" action="create">
            <Button
              variant="primary"
              icon={<Plus className="h-4 w-4" />}
              onClick={handleLogActivity}
              className="bg-agrosaf-green hover:bg-agrosaf-darkGreen"
            >
              Log Activity
            </Button>
          </PermissionGate>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
        <div className="flex flex-col xl:flex-row gap-3 xl:items-center">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by Customer ID, Customer Name, Company Name or Title..."
              value={searchTerm}
              onChange={(e) => {
                const sanitized = handleSearchInputChange(
                  e,
                  setSearchTerm,
                  FIELD_LIMITS.search,
                );
                setSearchTerm(sanitized);
              }}
              onKeyDown={(e) => {
                // Prevent form submission on Enter key
                if (e.key === "Enter") {
                  e.preventDefault();
                }
              }}
              maxLength={FIELD_LIMITS.search}
              autoFocus
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent w-full"
              autoComplete="off"
            />
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <select
              value={activeTab}
              onChange={(e) => setActiveTab(e.target.value)}
              className="w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
            >
              <option value="all">All Activities</option>
              <option value="call">Calls</option>
              <option value="email">Emails</option>
              <option value="whatsapp">WhatsApp</option>
              <option value="meeting">Meetings</option>
              <option value="note">Notes</option>
            </select>
            <select
              value={outcomeFilter}
              onChange={(e) => setOutcomeFilter(e.target.value)}
              className="w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent capitalize"
            >
              <option value="all">All Status</option>
              {activityStatuses.map((status) => (
                <option key={status._id || status.id} value={status._id || status.id}>
                  {status.name}
                </option>
              ))}
            </select>
            <select
              value={natureFilter}
              onChange={(e) => setNatureFilter(e.target.value)}
              className="w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
            >
              <option value="all">All Nature</option>
              <option value="past">Past Activity</option>
              <option value="followup">Follow Up</option>
            </select>
            {!isSalesExecutive && (
              <div className="relative">
                <button
                  ref={assigneeDropdownRef}
                  onClick={() => {
                    const isOpening = !openAssigneeDropdown;
                    setOpenAssigneeDropdown(isOpening);
                    if (isOpening) {
                      setFilteredAssigneeOptions(assigneeOptions);
                    }
                  }}
                  disabled={loadingAssignees}
                  className="px-3 py-2 border border-gray-300 rounded-lg flex items-center justify-between text-left bg-white focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                >
                  <span className="truncate text-sm whitespace-nowrap">
                    {assigneeFilter === 'all'
                      ? 'All Assignees'
                      : assigneeFilter === 'my_team'
                        ? 'My Team'
                        : assigneeOptions.find(opt => opt.id === assigneeFilter)?.name || 'All Assignees'}
                  </span>
                  <ChevronDown className={`w-4 h-4 text-gray-400 flex-shrink-0 ml-2 transition-transform ${openAssigneeDropdown ? 'rotate-180' : ''}`} />
                </button>

                {openAssigneeDropdown && (
                  <div
                    data-dropdown-assignee-filter
                    className="fixed z-[9999] bg-white border border-gray-200 rounded-lg shadow-xl"
                    style={{
                      top: `${assigneeDropdownPosition.top}px`,
                      left: `${assigneeDropdownPosition.left}px`,
                      minWidth: '240px',
                      maxWidth: '350px',
                      width: `${Math.max(240, assigneeDropdownPosition.width)}px`
                    }}
                  >
                    <div className="p-2 border-b border-gray-100">
                      <div className="relative">
                        <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type="text"
                          value={assigneeSearchTerm}
                          onChange={(e) => {
                            const value = e.target.value.replace(/[^a-zA-Z0-9\s@._+-]/g, '').slice(0, 100);
                            setAssigneeSearchTerm(value);
                          }}
                          placeholder="Search assignees..."
                          maxLength={100}
                          className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                          autoFocus
                        />
                      </div>
                    </div>
                    <div className="overflow-y-auto max-h-[240px] overflow-x-hidden">
                      {loadingAssignees && filteredAssigneeOptions.length === 0 ? (
                        <div className="p-3 text-sm text-gray-500 text-center">
                          Loading assignees...
                        </div>
                      ) : filteredAssigneeOptions.length === 0 ? (
                        <div className="p-3 text-sm text-gray-500 text-center">
                          {assigneeSearchTerm ? 'No assignees found' : 'No assignees available'}
                        </div>
                      ) : (
                        <div className="flex flex-col">
                          {filteredAssigneeOptions.map(option => (
                            <button
                              key={option.id}
                              type="button"
                              onClick={() => {
                                setAssigneeFilter(option.id);
                                setOpenAssigneeDropdown(false);
                                setAssigneeSearchTerm('');
                              }}
                              className={`w-full text-left px-3 py-2 text-sm border-b border-gray-100 last:border-b-0 flex flex-col ${assigneeFilter === option.id
                                ? 'bg-agrosaf-green/10 text-agrosaf-green font-medium'
                                : 'hover:bg-gray-50 text-gray-700'
                                }`}
                            >
                              <div className="truncate font-medium break-words">{option.name}</div>
                              {option.email && (
                                <div className="text-xs text-gray-500 truncate break-words">{option.email}</div>
                              )}
                              {option.role && (
                                <div className="text-xs text-gray-500 truncate capitalize break-words">{option.role}</div>
                              )}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
            <div className="flex-shrink-0">
              <DateRangeFilter
                startDate={dateRange.startDate}
                endDate={dateRange.endDate}
                onChange={setDateRange}
                allowFutureDates={true}
              />
            </div>
            {hasActiveFilters && (
              <Button
                variant="outline"
                icon={<X className="h-4 w-4" />}
                onClick={handleClearFilters}
                className="flex-shrink-0 whitespace-nowrap"
              >
                Clear
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Activities List - Table View */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-agrosaf-green mx-auto mb-4"></div>
            <p className="text-gray-600">Loading activities...</p>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full table-auto divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left w-12">
                    <input
                      type="checkbox"
                      className="h-4 w-4 text-agrosaf-green border-gray-300 rounded focus:ring-agrosaf-green"
                      checked={
                        activities.length > 0 &&
                        selectedRows.size === activities.length
                      }
                      onChange={handleSelectAll}
                    />
                  </th>

                  <th
                    scope="col"
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort("activityId")}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Activity ID</span>
                      {sortBy === "activityId" ? (
                        sortOrder === "asc" ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort("customerId")}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Customer ID</span>
                      {sortBy === "customerId" ? (
                        sortOrder === "asc" ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none max-w-[180px] sm:max-w-[220px]"
                    onClick={() => handleSort("customerName")}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Customer Name</span>
                      {sortBy === "customerName" ? (
                        sortOrder === "asc" ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort("title")}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Title</span>
                      {sortBy === "title" ? (
                        sortOrder === "asc" ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort("type")}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Activity Type</span>
                      {sortBy === "type" ? (
                        sortOrder === "asc" ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap"
                  >
                    Duration
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort("performedAt")}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Activity Date</span>
                      {sortBy === "performedAt" ? (
                        sortOrder === "asc" ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort("assignee")}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Assignee</span>
                      {sortBy === "assignee" ? (
                        sortOrder === "asc" ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap"
                  >
                    Status
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort("createdAt")}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Created</span>
                      {sortBy === "createdAt" ? (
                        sortOrder === "asc" ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap"
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {activities.length === 0 ? (
                  <tr>
                    <td colSpan="12" className="px-4 py-12 text-center">
                      <div className="flex flex-col items-center">
                        <div className="p-3 bg-gray-100 rounded-full mb-4">
                          <Clock className="h-6 w-6 text-gray-400" />
                        </div>
                        <h3 className="text-lg font-medium text-gray-900 mb-2">
                          No activities found
                        </h3>
                        <p className="text-gray-500 mb-4">
                          {searchTerm || activeTab !== "all"
                            ? "Try adjusting your filters or search terms."
                            : "No activities have been logged yet."}
                        </p>
                        <Button
                          variant="primary"
                          icon={<Plus className="h-4 w-4" />}
                          onClick={handleLogActivity}
                          className="bg-agrosaf-green hover:bg-agrosaf-darkGreen"
                        >
                          Log Activity
                        </Button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  getSortedActivities().map((activity, rowIndex) => {
                    const activityId = getActivityRowId(activity);
                    const isSelected = selectedRows.has(activityId);
                    const durationDisplay =
                      activity.duration ||
                        activity.callMetadata?.talkDuration ||
                        activity.callMetadata?.callDuration
                        ? formatDuration(
                          activity.duration,
                          activity.callMetadata?.talkDuration ||
                          activity.callMetadata?.callDuration,
                        )
                        : "N/A";
                    const customerDisplayId = activity.customer?.customerId || activity.customer?.id;
                    const customerNameDisplay = displayValue(getCustomerName(activity.customer));
                    const titleDisplay = displayValue(activity.title);
                    const typeDisplay = displayValue(activity.type);
                    const dateDisplay = displayValue(formatDate(getActivityTime(activity)));
                    const assigneeDisplay = displayValue(getAssigneeName(activity.performedBy));
                    const statusDisplay = displayValue(
                      activity.activityStatus?.name || activity.status || activity.outcome
                    );
                    const rawTime = getActivityTime(activity);
                    const actDate = rawTime ? new Date(rawTime) : null;
                    const isFollowUp = activity.activityNature === 'followup';
                    const isFutureActivity = actDate ? actDate.getTime() > Date.now() : false;

                    return (
                      <tr
                        key={activityId}
                        className={`hover:bg-gray-50 ${isSelected ? "bg-blue-50" : ""}`}
                      >
                        <td className="px-4 py-4 whitespace-nowrap align-top">
                          <input
                            type="checkbox"
                            className="h-4 w-4 text-agrosaf-green border-gray-300 rounded focus:ring-agrosaf-green"
                            checked={isSelected}
                            onChange={() => handleSelectRow(activityId)}
                          />
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap align-top">
                          <span
                            className="text-xs text-blue-600 font-medium font-mono cursor-pointer hover:text-blue-800 hover:underline"
                            onClick={() => handleViewActivity(activityId)}
                            title="View activity details"
                          >
                            {activity.activityId || 'N/A'}
                          </span>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap align-top">
                          {customerDisplayId ? (
                            <span
                              className="text-xs text-blue-600 font-medium font-mono cursor-pointer hover:text-blue-800 hover:underline"
                              onClick={() =>
                                handleViewCustomer(
                                  activity.customer?._id || activity.customer?.id,
                                )
                              }
                              title="View customer details"
                            >
                              {customerDisplayId}
                            </span>
                          ) : (
                            <span className="text-xs text-gray-500 font-mono">N/A</span>
                          )}
                        </td>
                        <td className="px-4 py-4 align-top max-w-[180px] sm:max-w-[220px]">
                          <div
                            className="text-sm font-medium text-gray-900 truncate min-w-0"
                            title={customerNameDisplay}
                          >
                            {customerNameDisplay}
                          </div>
                          {activity.customer?.companyName && activity.customer?.contactPerson && (
                            <div
                              className="text-xs text-gray-500 truncate min-w-0"
                              title={activity.customer.contactPerson}
                            >
                              {activity.customer.contactPerson}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-4 align-top">
                          <div
                            className="text-sm font-medium text-gray-900 truncate min-w-0 first-letter:capitalize"
                            title={titleDisplay}
                          >
                            {titleDisplay}
                          </div>
                        </td>
                        <td className="px-4 py-4 align-top">
                          <span className="text-sm text-gray-900 capitalize">
                            {typeDisplay}
                          </span>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700 align-top">
                          {durationDisplay}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600 align-top">
                          <div>{dateDisplay}</div>
                          <div className="mt-1">
                            {isFollowUp && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800 border border-amber-200">
                                Follow Up
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-4 align-top">
                          <div className="text-sm text-gray-900 truncate capitalize" title={assigneeDisplay}>
                            {assigneeDisplay}
                          </div>
                          {activity.performedBy?.email && (
                            <div className="text-xs text-gray-500 truncate lowercase" title={activity.performedBy.email}>
                              {activity.performedBy.email}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 align-top capitalize">
                          {statusDisplay}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600 align-top">
                          {activity.createdAt ? formatDate(activity.createdAt) : 'N/A'}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm font-medium align-top">
                          <div className="flex items-center space-x-2">
                            <PermissionGate
                              permission="customer_management"
                              action="read"
                            >
                              <button
                                onClick={() => handleViewActivity(activityId)}
                                className="text-blue-600 hover:text-blue-900"
                                title="View Details"
                              >
                                <Eye className="h-4 w-4" />
                              </button>
                            </PermissionGate>

                            {isSalesExecutive ? (
                              isFutureActivity && (
                                <button
                                  onClick={() => handleEditActivity(activity)}
                                  className="text-green-600 hover:text-green-900"
                                  title="Edit Activity"
                                >
                                  <Edit className="h-4 w-4" />
                                </button>
                              )
                            ) : (
                              <PermissionGate
                                permission="customer_management"
                                action="update"
                              >
                                <button
                                  onClick={() => handleEditActivity(activity)}
                                  className="text-green-600 hover:text-green-900"
                                  title="Edit Activity"
                                >
                                  <Edit className="h-4 w-4" />
                                </button>
                              </PermissionGate>
                            )}

                            {!isSalesExecutive && (
                              <PermissionGate
                                permission="customer_management"
                                action="delete"
                              >
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    // IMPORTANT: handleDeleteActivity will show confirmation FIRST
                                    // The API will ONLY be called AFTER user clicks OK in confirmation dialog
                                    // This is a blocking call - execution stops until user responds
                                    handleDeleteActivity(activityId);
                                  }}
                                  disabled={deletingActivityId === activityId}
                                  className="text-red-600 hover:text-red-900 disabled:opacity-50 disabled:cursor-not-allowed"
                                  title="Delete Activity"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </PermissionGate>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pagination - Same as Customer List & Filters */}
      {(() => {
        const activitiesTotalItems = Number(
          pagination?.totalActivities || activities.length || 0,
        );
        const activitiesTotalPages = Number(pagination?.totalPages || 1);
        const effectivePaginationLimit = pagination?.limit || itemsPerPage || 1;
        // Show pagination if there's more than one page
        const shouldShowPagination = !loading && activitiesTotalPages > 1;
        const handleActivitiesPageChange = (page) => {
          setCurrentPage(page);
          window.scrollTo({ top: 0, behavior: "smooth" });
        };

        return shouldShowPagination ? (
          <PaginationControls
            currentPage={pagination.currentPage || 1}
            totalPages={pagination.totalPages || 1}
            totalItems={activitiesTotalItems}
            limit={effectivePaginationLimit}
            hasNextPage={pagination.hasNextPage}
            hasPrevPage={pagination.hasPrevPage}
            onPageChange={handleActivitiesPageChange}
            onLimitChange={handleItemsPerPageChange}
            limitOptions={[10, 25, 50, 100]}
          />
        ) : null;
      })()}

      {/* Activity Detail Modal */}

      {/* Customer Selection Modal */}
      <CustomerSelectionModal
        isOpen={showCustomerSelectionModal}
        onClose={() => setShowCustomerSelectionModal(false)}
        onSelect={handleConfirmCustomerSelection}
      />
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        loading={exportLoading}
        title="Export Activities"
        scopedFormatExport={{
          onExport: handleCustomerActivitiesScopedExport,
        }}
      />

      {/* Bulk Delete Confirmation Modal */}
      {showBulkDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[110] !mt-0 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg mx-auto shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
                Bulk Delete Activities
              </h3>
              <button
                onClick={() => !isBulkDeleting && setShowBulkDeleteModal(false)}
                className="text-gray-400 hover:text-gray-600"
                disabled={isBulkDeleting}
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mb-6">
              <p className="text-sm text-gray-600">
                Are you sure you want to delete <span className="font-bold text-gray-900">{selectedRows.size}</span> selected activities? This action cannot be undone.
              </p>
            </div>

            <div className="flex items-center justify-end space-x-3">
              <Button
                variant="outline"
                onClick={() => setShowBulkDeleteModal(false)}
                disabled={isBulkDeleting}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                icon={isBulkDeleting ? <Clock className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                className="bg-red-600 hover:bg-red-700"
                onClick={confirmBulkDelete}
                disabled={isBulkDeleting}
              >
                {isBulkDeleting ? "Deleting..." : `Delete All Selected (${selectedRows.size})`}
              </Button>
            </div>
          </div>
        </div>
      )}


    </div>
  );
};

export default CustomerActivities;
